import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Attribute } from "@esWebAPI/es/model/Feature";
import type { Criteria } from "@esWebAPI/es/model/Criteria";

import AbstractFilterControl from "./AbstractFilterControl";

const NEGATIVE_LOGIC_OPERATORS = ["Not_Equals"];

type SubFilter = {
    type: string,
    value: any,
    range: Record<string, any>
    hasValue?: boolean
} | {
    type: "In_Range",
    range: Record<string, any>,
    minValue: number,
    maxValue: number,
    hasValue?: boolean
};

export default class NumericFilterControl extends AbstractFilterControl {
    private subFilters: SubFilter[];
    private range: any;
    private min: number;
    private max: number;
    private minValue: number;
    private maxValue: number;

    constructor(layerViewModel: LayerViewModel, attribute: Attribute, ranges) {
        super(layerViewModel, "Range", attribute);

        this.subFilters = [];
        this.range = {...ranges};
        this.minValue = this.min = this.range.min || 0;
        this.maxValue = this.max = this.range.max || 0;

        if (this.isEmpty(ranges)) {
            return;
        }

        // Format the range to have 2 decimal places
        if (attribute.attrType === "DECIMAL") {
            this.range.max = +ranges.max.toFixed(2);
            this.range.min = +ranges.min.toFixed(2);
            //We need to compare the numbers with 2 and the original to ensure that the
            // final 2 decimal places range encompasses the original range.
            if(this.range.min > ranges.min) {
                this.range.min = +(ranges.min - 0.01).toFixed(2);
            }
            if(this.range.max < ranges.max) {
                this.range.max = +(ranges.max + 0.01).toFixed(2);
            }
            // assign all vals with toFixed version
            this.minValue = this.min = this.range.min;
            this.maxValue = this.max = this.range.max;
        }
    }

    changeValue(value, index: number) {
        if (this.subFilters[index]){
            this.subFilters[index].value = value;
            this.update(this.subFilters);
        }
    }

    changeType(type: string, index: number) {
        if (this.subFilters[index]) {
            this.subFilters[index].type = type;
            this.update(this.subFilters);
        }
    }

    toCriteria(): Criteria {
        const criteria = this.getCriterias();

        if (criteria.length === 1) {
            return criteria[0];
        }

        const isNegativeLogic = this.subFilters.findIndex((c) => NEGATIVE_LOGIC_OPERATORS.includes(c.type)) !== -1;
        return {
            $type: isNegativeLogic ? "And" : "Or",
            criteria: criteria
        };
    }

    isNegativeLogic() {
        return this.subFilters.findIndex((c) => NEGATIVE_LOGIC_OPERATORS.includes(c.type)) !== -1;
    }

    fromCriteria(criteria: Criteria | Criteria[], attribute?: Attribute, range?: any) {

        // This code block is needed for TOR9-18175
        // If we have an old range criteria definition, the numeric filter comes in a array
        // rather than a $And criteria. This ensures we manage it correctly
        // Block Start
        if (!criteria.$type) {
            criteria = {$type: "And", criteria};
        }
        // Block End

        if (Array.isArray(criteria)) { // assuming multiple criteria
            this.subFilters = criteria.reduce((res, crit) => {
                if (crit.$type === "Or" || crit.$type === "And" && !this.isRangeFilter(crit, attribute)) {
                    Array.prototype.push.apply(res, crit.criteria.map(c => this.getSubFilter(c, attribute, range)));
                } else {
                    res.push(this.getSubFilter(crit, attribute, range));
                }
                return res;
            }, [] as SubFilter[]);
        } else if (criteria.$type === "Or" || criteria.$type === "And" && !this.isRangeFilter(criteria, attribute)) {
            this.subFilters = criteria.criteria.map((crit) => {
                return this.getSubFilter(crit, attribute, range);
            });
        } else {
            this.subFilters = [this.getSubFilter(criteria, attribute, range)];
        }
    }

    isValid() {
        return this.getActiveSubFilter().length > 0;
    }

    /**
     * Get "active" subfilter. A subFilter is considered active if its value (if any) is set.
     * If the user hasn't provide a value yet, the subfilter should not be transformed into a criteria, so it's considered inactive
     * @returns {Array.<T>}
     */
    getActiveSubFilter(): SubFilter[] {
        return this.subFilters.filter(function(filter) {
            return !filter.hasValue || !!filter.value || (!!filter.minValue && !!filter.maxValue);
        });
    }

    setRange(attributeRanges: Record<string, number[]>) {
        const range = attributeRanges[this.getAttrName()];
        if (this.isEmpty(range)){
            return;
        }

        this.min = range[0];
        this.max = range[1];
        var changed = false;
        if (this.minValue < this.min) {
            this.minValue = this.min;
            changed = true;
        }

        if (this.maxValue > this.max) {
            this.maxValue = this.max;
            changed = true;
        }

        if (!this.isLocked() && changed) {
            this.eventChannel.fireEvent("onUpdate");
        }
    }

    update(subFilters: SubFilter[], triggerRender = true) {
        this.subFilters = subFilters;

        this.eventChannel?.fireEvent("onUpdate", triggerRender); // there maybe no eventChannel if the filter is getting destroyed
    }

    addSubFilter(type: string, value) {
        if (type === "In_Range") {
            this.subFilters = [{
                type,
                minValue: this.minValue,
                maxValue: this.maxValue,
                range: this.range
            }]
        } else {
            this.subFilters.push({
                type: type,
                value: value,
                range: {
                    min: this.minValue,
                    max: this.maxValue
                }
            });
        }

        this.eventChannel.fireEvent("onUpdate");// needed for mobile maybe it will break eswebapp
    }

    removeSubFilter(index: number) {
        this.subFilters.splice(index, 1);

        this.eventChannel.fireEvent("onUpdate");
    }

    getCriterias(): Criteria[] {
        return this.getActiveSubFilter().map((filter) => {
            if (filter.type === "In_Range") {
                return this.createCriteria(filter.type, filter.minValue, filter.maxValue);
            }

            return this.createCriteria(filter.type, filter.value);
        });
    }

    createCriteria(operator: string, value: string | number, value2?: string | number): Criteria {
        switch (operator) {
            case 'Equals':
                return {
                    '$type': 'Equal',
                    'attr': this.attrName,
                    'value': +value
                };

            case 'Not_Equals':
                return {
                    '$type': 'NotEqual',
                    'attr': this.attrName,
                    'value': +value
                };
            case "In_Range":
                return {
                    $type: "And",
                    criteria: [{
                        $type: "GreaterEqual",
                        attr: this.attrName,
                        value: +value
                    }, {
                        $type: "LessEqual",
                        attr: this.attrName,
                        value: +value2
                    }]
                }
            default:
                throw new Error("IllegalState"); // forgot some case?!
        }
    }

    merge(filter) {
        const criteria = [this.toCriteria(true), filter.toCriteria(true)];
        this.fromCriteria(criteria);
    }

    private getSubFilter(criteria: Criteria, attribute?: Attribute, ranges?: any): SubFilter {
        if (this.isRangeFilter(criteria, attribute)) {
            const minValue = _.find(criteria.criteria, c => c.$type === "GreaterEqual").value;
            const maxValue = _.find(criteria.criteria, c => c.$type === "LessEqual").value;
            return {
                type: "In_Range",
                range: ranges,
                minValue,
                maxValue,
                hasValue: true
            };
        }

        const type = criteria.$type;
        const value = criteria.value;
        const subType = this.getFilterSubType(type, value);
        return {
            type: subType,
            value,
            range: ranges[attribute.name]
        };
    }

    private isRangeFilter(criteria: Criteria, attribute?: Attribute): boolean {
        if (!attribute) {
            return false;
        }
        
        return criteria.$type === "And"
                && criteria.criteria.every(subCriteria => subCriteria.$type === "GreaterEqual" || subCriteria.$type === "LessEqual")
                && criteria.criteria.every(subCriteria => subCriteria.attr === attribute.name)
    }

    private getFilterSubType(type: string, value): string | null {
        switch (type) {
            case "Equal":
                return "Equals"

            case "NotEqual":
                return "Not_Equals";

            default:
                es.log.error("Unsupported criteria type for Numeric Filter", type, value);
                return null;
        }
    }

    private isEmpty(range): boolean {
        return !(range.min !== undefined && range.max !== undefined);
    }


    static supportCriteria(criteria: Criteria, attribute?: Attribute) {
        return NumericFilterControl.isNumericFilter(criteria, attribute);
    }

    private static isNumericFilter(criteria: Criteria, attribute?: Attribute): boolean {
        if (!attribute) {
            return false;
        }
        
        let _criteria: Criteria[]; // convert the criteria to an array, so it is simpler to validate
        if (criteria.$type === "And" || criteria.$type === "Or") {
            _criteria = criteria.criteria;
        } else if (Array.isArray(criteria)) {
            // This code block is needed for TOR9-18175
            // If we have an old range criteria definition, the numeric filter comes in a array
            // rather than a $And criteria. This ensures we manage it correctly
            _criteria = criteria;
        } else {
           _criteria = [criteria]
        }

        return Array.isArray(_criteria)
                && _criteria.length >= 1
                && NumericFilterControl.supportAttribute(attribute.attrType)
                && _criteria.every(crit => crit.$type === "Equal" || crit.$type ===  "NotEqual" || crit.$type === "GreaterEqual" || crit.$type === "LessEqual")
                && _criteria.every(crit => crit.attr === attribute.name)
    }

    static match() {
        return false;
    }

    static supportAttribute(attrType: string): boolean {
        return (attrType === "DECIMAL" || attrType === "INTEGER");
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria, attribute: Attribute, ranges: Record<string, any>): JQueryDeferred<NumericFilterControl> {
        return new NumericFilterControl(layerViewModel, attribute, ranges[attribute.name]).init().done((filter: NumericFilterControl) => {
            filter.fromCriteria(criteria, attribute, ranges);
        });
    }

    // es.control.NumericFilter = NumericFilter;
}
